import { EventEmitter } from './event-emitter';
import {
    IPC_EXTERNAL_ERROR,
    IPC_SUPPORTED_COMMANDS,
    IPC_SUPPORTED_EVENTS,
} from './constants/ipc-action-types';
import diContainer, { setDiValues } from './di-container';
import RwcOverrideAPI from './override-api';
import { singletonGlobal } from './singleton';

export enum InternalApiStatus {
    nonInitialized = 'non-initialized',
    idle = 'idle',
    inMeeting = 'in-meeting',
    crashed = 'crashed',
}

export class RwcInternalApi extends EventEmitter {
    private status: InternalApiStatus = InternalApiStatus.nonInitialized;
    subscriptions: { [key: string]: Array<(args: unknown) => void> } = {};
    overrideApi = RwcOverrideAPI;
    diContainer = diContainer;
    setDiValues = setDiValues;

    register<Api>(name: IntApiKey, method: Api, force = false) {
        if (Object.prototype.hasOwnProperty.call(this, name) && !force) return;

        Object.defineProperty(this, name, {
            configurable: true,
            get() {
                return method;
            },
        });
        const subscriptions = this.subscriptions[name] || [];
        subscriptions.forEach(cb => cb(method));
        delete this.subscriptions[name];
    }

    registerOrExtend<Api>(name: IntApiKey, method: Api) {
        if (!Object.prototype.hasOwnProperty.call(this, name) || typeof method !== 'object') {
            return this.register(name, method);
        }

        const previousValue = this[name];
        this.register(name, { ...previousValue, ...method }, true);
    }

    subscribeToMethod(name) {
        return new Promise(resolve => {
            if (Object.prototype.hasOwnProperty.call(this, name)) return resolve(this[name]);

            this.subscriptions[name] = [...(this.subscriptions[name] || []), resolve];
        });
    }

    unregister(name: IntApiKey) {
        // TODO need to rework unregister
        try {
            delete this[name];
            // eslint-disable-next-line no-empty
        } catch (e) {}
    }

    emit(event, ...args) {
        const logger = this[intApiLogger];
        if (logger && this.supportedEvents.has(event) && event !== IPC_EXTERNAL_ERROR) {
            logger.debug('Internal api event emitted:', event, args);
        }
        super.emit(event, ...args);
    }

    supportedEvents = new Set(IPC_SUPPORTED_EVENTS);

    supportedCommands = new Set(IPC_SUPPORTED_COMMANDS);

    getStatus() {
        return this.status;
    }

    setStatus(status: InternalApiStatus) {
        // setStatus will be used outside TS, so lets typeproof an argument
        if (Object.values(InternalApiStatus).includes(status)) {
            this.status = status;
        }
    }
}

export const changeRwcMountPoint = 'changeRwcMountPoint';
export const changeRwcModalsMountPoint = 'changeRwcModalsMountPoint';
export const changeRwcBannersMountPoint = 'changeRwcBannersMountPoint';

export const appInitialized = 'appInitialized';

export const intApiReduxStore = 'store';
export const intApiRcSdk = 'sdk';
export const intApiLibRct = 'librct';
export const intApiLibSfu = 'libsfu';
export const intApiNetworkAggregator = 'networkAggregator';
export const intApiEdrClient = 'edrClient';
export const intApiCprClient = 'cprClient';
export const intApiBrandConfig = 'intApiBrandConfig';
export const intApiExternalConfig = 'externalConfig';
export const intApiExtraData = 'intApiExtraData';
export const intApiExternalComponents = 'externalComponents';
export const intApiGetPlatformToken = 'intApiGetPlatformToken';
export const intApiLogger = 'intApiLogger';
export const overrideApi = 'overrideApi';
export const intDiContainer = 'diContainer';
export const intSetDiValues = 'setDiValues';
export const intWebpackPublicPath = 'intWebpackPublicPath';
export const clientCommunicationProvider = 'clientCommunicationProvider';
export const intDeferredPerformanceEvents = 'intDeferredPerformanceEvents';
export const intInstantPerformanceEvents = 'intInstantPerformanceEvents';
export const intApiExternalSentry = 'externalSentry';
export const intApiSyncSettings = 'syncSettings';
export const intApiGetPrefixedLogger = 'getPrefixedLogger';
export const intSSSenderPerformanceEvents = 'intSSSenderPerformanceEvents';
export const intSSReceiverPerformanceEvents = 'intSSReceiverPerformanceEvents';
export const intJoinMeetingPerformanceEvents = 'intJoinMeetingPerformanceEvents';
export const intApiFeatureFlagClient = 'featureFlagClient';

type IntApiKey =
    | typeof intApiReduxStore
    | typeof intApiRcSdk
    | typeof intApiLibRct
    | typeof intApiLibSfu
    | typeof intApiNetworkAggregator
    | typeof intApiEdrClient
    | typeof intApiCprClient
    | typeof intApiBrandConfig
    | typeof intApiExternalConfig
    | typeof intApiExtraData
    | typeof intApiExternalComponents
    | typeof intApiGetPlatformToken
    | typeof intApiLogger
    | typeof overrideApi
    | typeof intDiContainer
    | typeof intSetDiValues
    | typeof intWebpackPublicPath
    | typeof clientCommunicationProvider
    | typeof intDeferredPerformanceEvents
    | typeof intInstantPerformanceEvents
    | typeof intApiExternalSentry
    | typeof intApiSyncSettings
    | typeof intApiGetPrefixedLogger
    | typeof intSSSenderPerformanceEvents
    | typeof intSSReceiverPerformanceEvents
    | typeof intJoinMeetingPerformanceEvents
    | typeof intApiFeatureFlagClient;

const rwcInternalApi =
    typeof window === 'object'
        ? singletonGlobal('rwcInternalApi', RwcInternalApi)()
        : new RwcInternalApi();

export default rwcInternalApi;

export interface ISSReceiverEventsReporter {
    trackStream(stream: string | undefined, timestamp: number): void;
}

export interface ISSSenderEventsReporter {
    record(point: string, timestamp: number): void;
    reportStopSS(): void;
    send(error?: string): void;
    start(apiVer: 'v1' | 'v2'): void;
}

export const getSSReceiverPerformanceEvents = () =>
    rwcInternalApi[intSSReceiverPerformanceEvents] as ISSReceiverEventsReporter;

export const getSSSenderPerformanceEvents = () =>
    rwcInternalApi[intSSSenderPerformanceEvents] as ISSSenderEventsReporter;
